// src/components/QuickReplySelector.jsx
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  getQuickReplies,
  createQuickReply,
  updateQuickReply,
  deleteQuickReply,
  getQuickReplyStats
} from '../services/quickReplyService';

export default function QuickReplySelector({ market, onSelect }) {
  const { t } = useTranslation();

  const [quickReplies, setQuickReplies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Estados para gestión
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [editingReply, setEditingReply] = useState(null);
  const [stats, setStats] = useState(null);

  // Form para crear/editar
  const [formData, setFormData] = useState({
    shortcut: '',
    title: '',
    content: '',
    market: market || 'es',
  });

  useEffect(() => {
    loadQuickReplies();
  }, [market]);

  const loadQuickReplies = async (search = '') => {
    try {
      setLoading(true);
      setError('');
      const data = await getQuickReplies(market, search || null);
      setQuickReplies(data);
    } catch (err) {
      console.error('[QuickReplySelector] Error loading quick replies:', err);
      setError(t('quickReplies.error.load') || 'Error cargando respuestas rápidas');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await getQuickReplyStats(market);
      setStats(data);
    } catch (err) {
      console.error('[QuickReplySelector] Error loading stats:', err);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    loadQuickReplies(value);
  };

  const handleQuickReplyClick = (quickReply) => {
    if (onSelect) {
      onSelect(quickReply.content);
    }
  };

  const handleCreateNew = () => {
    setEditingReply(null);
    setFormData({
      shortcut: '',
      title: '',
      content: '',
      market: market || 'es',
    });
    setManageModalOpen(true);
  };

  const handleEdit = (quickReply) => {
    setEditingReply(quickReply);
    setFormData({
      shortcut: quickReply.shortcut,
      title: quickReply.title,
      content: quickReply.content,
      market: quickReply.market,
    });
    setManageModalOpen(true);
  };

  const handleSave = async () => {
    try {
      setError('');

      // Validaciones
      if (!formData.shortcut.trim()) {
        setError(t('quickReplies.error.shortcutRequired') || 'El atajo es obligatorio');
        return;
      }

      if (!formData.title.trim()) {
        setError(t('quickReplies.error.titleRequired') || 'El título es obligatorio');
        return;
      }

      if (!formData.content.trim()) {
        setError(t('quickReplies.error.contentRequired') || 'El contenido es obligatorio');
        return;
      }

      // Verificar que el shortcut sea lowercase
      const lowerShortcut = formData.shortcut.toLowerCase();
      if (formData.shortcut !== lowerShortcut) {
        setError(t('quickReplies.error.lowercaseRequired') || 'El atajo debe estar en minúsculas');
        return;
      }

      if (editingReply) {
        // Actualizar
        await updateQuickReply(editingReply.id, formData);
      } else {
        // Crear
        await createQuickReply(formData);
      }

      setManageModalOpen(false);
      loadQuickReplies(searchTerm);
    } catch (err) {
      console.error('[QuickReplySelector] Error saving:', err);
      setError(err.message || (t('quickReplies.error.save') || 'Error guardando respuesta rápida'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('quickReplies.confirm.delete') || '¿Eliminar esta respuesta rápida?')) {
      return;
    }

    try {
      await deleteQuickReply(id);
      loadQuickReplies(searchTerm);
    } catch (err) {
      console.error('[QuickReplySelector] Error deleting:', err);
      setError(err.message || (t('quickReplies.error.delete') || 'Error eliminando respuesta rápida'));
    }
  };

  const handleShowStats = async () => {
    await loadStats();
    setStatsModalOpen(true);
  };

  // Modal de gestión (crear/editar)
  const manageModal = manageModalOpen && createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
      onClick={() => setManageModalOpen(false)}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 relative max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={() => setManageModalOpen(false)}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-2xl"
        >
          ✕
        </button>

        <h2 className="text-2xl font-semibold mb-6">
          {editingReply
            ? (t('quickReplies.edit.title') || 'Editar respuesta rápida')
            : (t('quickReplies.create.title') || 'Nueva respuesta rápida')}
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('quickReplies.field.shortcut') || 'Atajo'} *
            </label>
            <input
              type="text"
              value={formData.shortcut}
              onChange={(e) => setFormData({ ...formData, shortcut: e.target.value.toLowerCase() })}
              placeholder="hello"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              maxLength={50}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('quickReplies.field.shortcut.hint') || 'Ej: hello, bye, info (solo minúsculas)'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('quickReplies.field.title') || 'Título'} *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder={t('quickReplies.field.title.placeholder') || 'Saludo inicial'}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              maxLength={100}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('quickReplies.field.content') || 'Contenido'} *
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder={t('quickReplies.field.content.placeholder') || 'Hola, ¿en qué puedo ayudarte?'}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={5}
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.content.length} caracteres
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('quickReplies.field.market') || 'Mercado'}
            </label>
            <select
              value={formData.market}
              onChange={(e) => setFormData({ ...formData, market: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="es">España (ES)</option>
              <option value="it">Italia (IT)</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={() => setManageModalOpen(false)}
            className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
          >
            {t('common.cancel') || 'Cancelar'}
          </button>
          <button
            onClick={handleSave}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            {t('common.save') || 'Guardar'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );

  // Modal de estadísticas
  const statsModal = statsModalOpen && stats && createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
      onClick={() => setStatsModalOpen(false)}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 relative max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={() => setStatsModalOpen(false)}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-2xl"
        >
          ✕
        </button>

        <h2 className="text-2xl font-semibold mb-6">
          {t('quickReplies.stats.title') || 'Estadísticas de uso'}
        </h2>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-3xl font-bold text-blue-600">{stats.total}</div>
            <div className="text-sm text-gray-600">{t('quickReplies.stats.total') || 'Total de respuestas'}</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-3xl font-bold text-green-600">{stats.totalUsage}</div>
            <div className="text-sm text-gray-600">{t('quickReplies.stats.totalUsage') || 'Usos totales'}</div>
          </div>
        </div>

        {Object.keys(stats.byMarket).length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">
              {t('quickReplies.stats.byMarket') || 'Por mercado'}
            </h3>
            <div className="space-y-2">
              {Object.entries(stats.byMarket).map(([marketCode, data]) => (
                <div key={marketCode} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="font-medium">{marketCode.toUpperCase()}</span>
                  <div className="text-sm text-gray-600">
                    {data.count} respuestas • {data.usage} usos
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {stats.mostUsed && stats.mostUsed.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-3">
              {t('quickReplies.stats.mostUsed') || 'Más utilizadas'}
            </h3>
            <div className="space-y-2">
              {stats.mostUsed.map((qr, index) => (
                <div key={qr.id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <div>
                    <span className="font-bold text-gray-400 mr-2">#{index + 1}</span>
                    <span className="font-mono text-sm bg-gray-200 px-2 py-1 rounded">{qr.shortcut}</span>
                    <span className="ml-2 text-gray-700">{qr.title}</span>
                  </div>
                  <span className="text-sm font-semibold text-blue-600">{qr.usageCount} usos</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );

  if (loading && quickReplies.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        {t('common.loading') || 'Cargando...'}
      </div>
    );
  }

  return (
    <>
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-sm font-semibold text-gray-700">
            {t('quickReplies.title') || 'Respuestas Rápidas'}
          </h4>
          <div className="flex gap-1">
            <button
              onClick={handleShowStats}
              className="text-xs px-2 py-1 text-blue-600 hover:bg-blue-50 rounded"
              title={t('quickReplies.stats.title') || 'Ver estadísticas'}
            >
              📊
            </button>
            <button
              onClick={handleCreateNew}
              className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              + {t('quickReplies.create.button') || 'Nueva'}
            </button>
          </div>
        </div>

        {/* Búsqueda */}
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearch}
          placeholder={t('quickReplies.search.placeholder') || 'Buscar...'}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded mb-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />

        {error && (
          <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
            {error}
          </div>
        )}

        {quickReplies.length === 0 ? (
          <p className="text-sm text-gray-500">
            {searchTerm
              ? (t('quickReplies.empty.search') || 'No se encontraron resultados')
              : (t('quickReplies.empty') || 'No hay respuestas rápidas')}
          </p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {quickReplies.map(quickReply => (
              <div
                key={quickReply.id}
                className="bg-white border border-gray-300 rounded hover:border-blue-400 transition-colors"
              >
                <button
                  onClick={() => handleQuickReplyClick(quickReply)}
                  className="w-full text-left px-3 py-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded text-blue-600">
                          /{quickReply.shortcut}
                        </span>
                        <span className="text-sm font-medium text-gray-800">
                          {quickReply.title}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 truncate mt-1">
                        {quickReply.content.substring(0, 60)}...
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      {quickReply.usageCount > 0 && (
                        <span className="text-xs text-gray-400">
                          {quickReply.usageCount}x
                        </span>
                      )}
                    </div>
                  </div>
                </button>
                <div className="border-t border-gray-200 px-3 py-1 flex justify-end gap-2">
                  <button
                    onClick={() => handleEdit(quickReply)}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    {t('common.edit') || 'Editar'}
                  </button>
                  <button
                    onClick={() => handleDelete(quickReply.id)}
                    className="text-xs text-red-600 hover:text-red-800"
                  >
                    {t('common.delete') || 'Eliminar'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {manageModal}
      {statsModal}
    </>
  );
}
